import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useMiniappSdk, useMiniappSdkEventHandler } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { setHostInfo } from 'store/host-info/actions';

/**
 * Asks the host what it is, once, and records the answer.
 *
 * Register this in `app` and NOWHERE else. `sdk.on` has no counterpart -- the SDK pushes handlers
 * onto an array with no way to remove one -- so a listener registered in anything that remounts
 * accumulates a copy per mount, every one of which fires.
 *
 * Asked at app mount rather than from the page that needs the answer, so the round trip happens
 * behind the loading skeleton. A page reading the platform is therefore almost never rendering
 * before the host has replied, which is what keeps chrome from appearing and then vanishing.
 */
export const useSyncHostInfoWithHostApp = (): void => {
  const client = useMiniappSdk();
  const dispatch = useDispatch();

  useMiniappSdkEventHandler(
    'getHostInfoResponse',
    (data: unknown) => {
      /*
       * The payload should be an object, but a non-empty string can slip through -- a double-encoded
       * payload the SDK unwraps only once. Reaching into a string for a field throws, so narrow to an
       * object before reading either answer out of it. Either way the host HAS answered, so this
       * settles.
       *
       * The VALUES are checked too, not just the keys. This arrives over a postMessage bridge from
       * another origin, so it is input rather than state this app wrote -- an assertion to
       * `{ platform?: string }` describes what we hope for and verifies nothing. Anything that is
       * not a string is no platform, and reads as `null` rather than being stored and handed to
       * consumers typed as a `HostPlatform` it is not.
       */
      const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;

      const platform = typeof payload?.platform === 'string' ? payload.platform : null;

      /*
       * ⭐ THE NATIVE SHELL IS THE ONE HOST THAT ANSWERS IN THE SDK'S OWN SHAPE, and that is what
       * identifies it. `GetHostInfoHandlerPayload` is `{ host: HostInfo }` -- `os_name`, `os_version`,
       * `device_name`, `application_version` and so on -- with no `platform` anywhere in it. Both
       * native shells build exactly that and nothing else (`MiniAppHostInfoProviderImpl.getHostInfo`
       * on Android, `HostJS(host:)` in miniapp-sdk-ios); both WEB hosts ignore the documented shape
       * and answer with a flat `{ platform, version, region, theme }` instead (tenant-portal sends
       * its package name, tenant-web sends `'web'`). The two shapes are disjoint, so the presence of
       * a named OS is a positive identification rather than a guess.
       *
       * ⛔ WHICH IS WHY THIS IS NOT `platform === null`. That is the SAME value a host that never
       * answers leaves behind, and a shell we cannot identify is not a shell we may assume draws a
       * header -- the pages read this to decide whether to draw their own way out.
       *
       * Checked by SHAPE, not by value. `os_name` is 'Android' on one side and `UIDevice.systemName`
       * on the other, and nothing here needs to know which: the question is "does a native shell
       * frame this app", and any native shell naming any OS has answered it. `HOST_PLATFORMS` says
       * not to guess a host's string from source, and this does not have to.
       */
      const host = payload?.host && typeof payload.host === 'object' ? (payload.host as Record<string, unknown>) : null;
      const isNativeApp = typeof host?.os_name === 'string' && host.os_name.length > 0;

      dispatch(setHostInfo({ platform, isNativeApp }));
    },
    [],
  );

  const hasAskedRef = useRef(false);

  useEffect(() => {
    if (!client || hasAskedRef.current) return;

    hasAskedRef.current = true;
    client.host.getInfo();
  }, [client]);
};
